import React, { useCallback, useRef, useState } from "react";
import { Field, useFormikContext } from "formik";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { AlertCircle } from "lucide-react";
import { url } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import { useEffect } from "react";

/* ==========================================================================
   API FETCH HELPER (WITH DEBOUNCE SUPPORT)
   ========================================================================== */
const fetchDynamicOptions = async (reference, inputValue = "") => {
  try {
    const response = await axios.post(
      `${url}/model/${reference.tableName}/list`,
      {
        ...(inputValue ? { name: inputValue } : {}),
        page: 1, // Defaulting to first page for standard AsyncSelect
      },
    );

    const rows = response.data.data?.rows || [];

    return rows.map((row) => ({
      label: `${row[reference.displayKey]} : ${row[reference.primaryKey]}`,
      value: row[reference.primaryKey],
      raw: row, // Retain original row object if needed downstream
    }));
  } catch (err) {
    console.error("API Fetch Error:", err);
    return [];
  }
};

/* ==========================================================================
   CUSTOM HOOK FOR DEBOUNCED SEARCH
   ========================================================================== */
const useDebouncedLoadOptions = (reference, delay = 300) => {
  const timeoutRef = useRef(null);

  return useCallback(
    (inputValue, callback) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(async () => {
        const options = await fetchDynamicOptions(reference, inputValue);
        callback(options);
      }, delay);
    },
    [reference, delay],
  );
};

/* ==========================================================================
   DYNAMIC SINGLE DROPDOWN
   ========================================================================== */
export const FormDropDownDynamic = ({
  name = "select",
  label = "Select Field",
  required = false,
  placeholder = "Search...",
  reference,
  className = "",
  ...props
}) => {
  const loadOptionsHandler = useDebouncedLoadOptions(reference);

  return (
    <div className={`w-full ${className}`}>
      <Field name={name}>
        {({ field, form, meta }) => {
          const hasError = Boolean(meta.touched && meta.error);

          // Resolve display object vs primitive scalar
          const selectValue = field.value
            ? typeof field.value === "object"
              ? field.value
              : { label: String(field.value), value: field.value }
            : null;

          return (
            <div className="flex flex-col gap-1.5 w-full">
              {label && (
                <label
                  htmlFor={name}
                  className={`block text-xs font-semibold tracking-wide uppercase transition-colors ${
                    hasError ? "text-red-500" : "text-gray-600"
                  }`}
                >
                  {formatUnderscoreToSpace(label)}
                  {required && <span className="ml-0.5 text-red-500">*</span>}
                </label>
              )}

              <div className="relative">
                <AsyncSelect
                  {...field}
                  {...props}
                  cacheOptions
                  defaultOptions
                  instanceId={name}
                  name={name}
                  loadOptions={loadOptionsHandler}
                  placeholder={placeholder}
                  isClearable
                  isSearchable
                  value={selectValue}
                  onChange={(selectedOption) => {
                    form.setFieldValue(
                      name,
                      selectedOption ? selectedOption.value : "",
                    );
                  }}
                  onBlur={() => form.setFieldTouched(name, true)}
                  styles={getCustomStyles(hasError)}
                />
              </div>

              <div className="min-h-[1.125rem] px-0.5">
                {hasError && (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-500">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {meta.error}
                  </span>
                )}
              </div>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

/* ==========================================================================
   DYNAMIC MULTI DROPDOWN
   ========================================================================== */
export const FormMultiDropDownDynamic = ({
  name = "select",
  label = "Select Field",
  required = false,
  placeholder = "Search and select...",
  reference,
  className = "",
  menuPlacement = "bottom",
  ...props
}) => {
  const loadOptionsHandler = useDebouncedLoadOptions(reference);
  const [selectedOptionsMap, setSelectedOptionsMap] = useState({});
  const { values } = useFormikContext();

  // Extract raw value for this field directly from Formik context
  const rawValues = Array.isArray(values?.[name]) ? values[name] : [];

  // Top-level useEffect to fetch missing display labels for Edit mode
  useEffect(() => {
    const missingIds = rawValues.filter(
      (val) => typeof val !== "object" && !selectedOptionsMap[val],
    );

    if (missingIds.length > 0 && reference) {
      fetchDynamicOptions(reference, "").then((options) => {
        const newMap = { ...selectedOptionsMap };
        options.forEach((opt) => {
          newMap[opt.value] = opt;
        });
        setSelectedOptionsMap(newMap);
      });
    }
  }, [JSON.stringify(rawValues), reference]);

  return (
    <div className={`w-full ${className}`}>
      <Field name={name}>
        {({ field, form, meta }) => {
          const hasError = Boolean(meta.touched && meta.error);

          // Map string IDs back to full option objects from state
          const selectedValues = rawValues.map((val) => {
            if (typeof val === "object" && val !== null) return val;
            return (
              selectedOptionsMap[val] || {
                label: String(val),
                value: val,
              }
            );
          });

          return (
            <div className="flex flex-col gap-1.5 w-full">
              {label && (
                <label
                  htmlFor={name}
                  className={`block text-xs font-semibold tracking-wide uppercase transition-colors ${
                    hasError ? "text-red-500" : "text-gray-600"
                  }`}
                >
                  {formatUnderscoreToSpace(label)}
                  {required && <span className="ml-0.5 text-red-500">*</span>}
                </label>
              )}

              <div className="relative">
                <AsyncSelect
                  {...field}
                  {...props}
                  cacheOptions
                  defaultOptions
                  isMulti
                  instanceId={name}
                  name={name}
                  loadOptions={loadOptionsHandler}
                  placeholder={placeholder}
                  isClearable
                  isSearchable
                  value={selectedValues}
                  onChange={(selectedOptions) => {
                    const options = selectedOptions || [];

                    // 1. Cache option objects locally for display labels
                    const newMap = { ...selectedOptionsMap };
                    options.forEach((opt) => {
                      newMap[opt.value] = opt;
                    });
                    setSelectedOptionsMap(newMap);

                    // 2. Pass array of string IDs to Formik
                    form.setFieldValue(
                      name,
                      options.map((opt) => opt.value),
                    );
                  }}
                  menuPlacement={menuPlacement}
                  onBlur={() => form.setFieldTouched(name, true)}
                  styles={getCustomMultiStyles(hasError)}
                />
              </div>

              <div className="min-h-[1.125rem] px-0.5">
                {hasError && (
                  <span className="flex items-center gap-1 text-xs font-medium text-red-500">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {meta.error}
                  </span>
                )}
              </div>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

/* ==========================================================================
   REACT SELECT STYLING CONFIGURATIONS
   ========================================================================== */
const getCustomStyles = (hasError) => ({
  indicatorSeparator: () => ({ display: "none" }),
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "0.5rem",
    backgroundColor: "#ffffff",
    borderColor: hasError ? "#fca5a5" : state.isFocused ? "#6366f1" : "#e5e7eb",
    borderWidth: "1px",
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 2px rgba(239, 68, 68, 0.2)"
        : "0 0 0 2px rgba(99, 102, 241, 0.2)"
      : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "&:hover": {
      borderColor: hasError ? "#ef4444" : "#d1d5db",
    },
    fontSize: "0.875rem",
    cursor: "pointer",
    transition: "all 150ms ease",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 0.75rem",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "0.875rem",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontSize: "0.875rem",
    fontWeight: "500",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "0.5rem",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f3f4f6",
    overflow: "hidden",
    zIndex: 50,
  }),
  menuList: (base) => ({
    ...base,
    padding: "0.25rem",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "0.375rem",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    fontWeight: state.isSelected ? "600" : "400",
    color: state.isSelected
      ? "#ffffff"
      : state.isFocused
        ? "#111827"
        : "#374151",
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
        ? "#f3f4f6"
        : "transparent",
    cursor: "pointer",
  }),
});

const getCustomMultiStyles = (hasError) => ({
  ...getCustomStyles(hasError),
  control: (base, state) => ({
    ...getCustomStyles(hasError).control(base, state),
    padding: "1px 0",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 0.5rem",
    gap: "4px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e0e7ff",
    borderRadius: "0.375rem",
    padding: "1px 4px",
    margin: "1px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#3730a3",
    fontSize: "0.75rem",
    fontWeight: "600",
    paddingLeft: "4px",
    paddingRight: "4px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#6366f1",
    borderRadius: "0.25rem",
    cursor: "pointer",
    transition: "all 150ms ease",
    "&:hover": {
      backgroundColor: "#c7d2fe",
      color: "#1e1b4b",
    },
  }),
});
